import { ActionIcon, FileButton, Input, ScrollArea } from "@mantine/core";
import { Link, RichTextEditor, RichTextEditorProps } from "@mantine/tiptap";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useCallback, useRef, useState } from "react";
import { IFocusableFormField } from "@type";
import ImageExtension from "@tiptap/extension-image";
import { IconPhoto } from "@tabler/icons-react";

export interface IFormRichTextFieldProps<Values = Record<string, unknown>>
  extends Omit<RichTextEditorProps, "editor" | "children">,
    IFocusableFormField<Values> {
  enableImgExtension?: boolean;
  getImageData?: any;
}

export const FormRichTextField: <Values = Record<string, unknown>>(
  props: IFormRichTextFieldProps<Values>,
) => JSX.Element = ({
  getImageData,
  enableImgExtension,
  fieldName,
  parentForm,
  ...rest
}) => {
  const { onChange, value, error, onFocus, onBlur } = parentForm.getInputProps(
    fieldName,
  ) as any;

  const [content, setContent] = useState(value);

  const editor = useEditor(
    {
      extensions: [StarterKit, Underline, Link, ImageExtension],
      content: content,
      onUpdate: ({ editor }) => {
        const value = editor.isEmpty ? "" : editor.getHTML();
        setContent(value);
        onChange(value);
      },
    },
    [],
  );

  if (value !== content) {
    if (editor && (content || !editor.isEmpty)) {
      editor.commands.setContent(value);
    }
    setContent(value);
  }

  const handleImageOnchange = useCallback(
    async (file: any) => {
      if (file && editor) {
        let imageSource = null;
        await getImageData(file).then((data: any) => {
          imageSource = data;
        });

        editor
          .chain()
          .focus()
          .setImage({ src: imageSource ? imageSource : "" })
          .run();
        const value = editor.isEmpty ? "" : editor.getHTML();
        setContent(value);
      }
    },
    [editor],
  );

  const elementRef = useRef<HTMLDivElement>(null);
  parentForm.registerField(fieldName, elementRef);

  return (
    <Input.Wrapper error={error} ref={elementRef}>
      <RichTextEditor
        {...rest}
        onFocus={onFocus}
        onBlur={onBlur}
        editor={editor}
      >
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>

          {enableImgExtension && (
            <RichTextEditor.ControlsGroup>
              <FileButton onChange={handleImageOnchange} accept="image/*">
                {(props) => (
                  <ActionIcon {...props}>
                    <IconPhoto color="gray" strokeWidth={1} />
                  </ActionIcon>
                )}
              </FileButton>
            </RichTextEditor.ControlsGroup>
          )}
        </RichTextEditor.Toolbar>
        <ScrollArea h={200} type="auto">
          <RichTextEditor.Content />
        </ScrollArea>
      </RichTextEditor>
    </Input.Wrapper>
  );
};
